import { useEffect, useMemo, useState } from 'react';
import type { Accent, Mode, Part } from '../types';
import { loadBank, topicsForPart } from '../lib/questions';
import { effectiveTts } from '../lib/keys';
import { ACCENT_LABEL } from '../lib/providers';
import { defaultConfig, type SessionConfig } from '../lib/session-controller';
import { APP_VERSION } from '../version';

const MODES: { id: Mode; title: string; desc: string }[] = [
  { id: 'part1_drill', title: 'Part 1 · Rapid fire', desc: 'Quick personal questions, answered on the spot.' },
  { id: 'part3_discussion', title: 'Part 3 · Discussion', desc: 'Abstract follow-ups that push you to explain.' },
  { id: 'full_mock', title: 'Full mock', desc: 'Parts 1 → 2 → 3 in order, like the real test.' },
  { id: 'endless', title: 'Endless', desc: 'Part 1 questions with no finish line — build stamina.' },
];

const ACCENTS: Accent[] = ['en-GB', 'en-US', 'en-AU'];

// Which part's topics a mode draws from (full mock spans all parts → no topics).
function partForMode(mode: Mode): Part | null {
  if (mode === 'part3_discussion') return 3;
  if (mode === 'full_mock') return null;
  return 1; // part1_drill, endless
}

export default function Setup({
  onStart,
  onOpenSettings,
  onOpenHelp,
}: {
  onStart: (cfg: SessionConfig) => void;
  onOpenSettings: () => void;
  onOpenHelp: () => void;
}) {
  const [mode, setMode] = useState<Mode>('part1_drill');
  const [accent, setAccent] = useState<Accent>('en-GB');
  const [allTopics, setAllTopics] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [count, setCount] = useState(12);

  const topicPart = partForMode(mode);
  const showTopics = topicPart !== null;
  const showCount = mode === 'part1_drill' || mode === 'part3_discussion';

  // Load topics for the current mode's part; reset the selection to all.
  useEffect(() => {
    if (topicPart === null) {
      setAllTopics([]);
      setSelected(new Set());
      return;
    }
    let live = true;
    loadBank()
      .then((bank) => {
        if (!live) return;
        const t = topicsForPart(bank, topicPart);
        setAllTopics(t);
        setSelected(new Set(t));
      })
      .catch(() => setAllTopics([]));
    return () => {
      live = false;
    };
  }, [topicPart]);

  const tts = useMemo(() => effectiveTts(), []);

  function begin() {
    const topics = !showTopics || selected.size === allTopics.length ? [] : [...selected];
    if (showTopics && allTopics.length && selected.size === 0) return; // nothing selected
    onStart(defaultConfig(mode, accent, topics, count));
  }

  // Space starts the drill (unless a field/modal/button has focus).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code !== 'Space') return;
      const el = document.activeElement as HTMLElement | null;
      if (el && ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes(el.tagName)) return;
      if (document.querySelector('.modal')) return;
      e.preventDefault();
      begin();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, accent, selected, allTopics, count]);

  function toggleTopic(t: string) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(t)) n.delete(t);
      else n.add(t);
      return n;
    });
  }

  const canStart = !showTopics || allTopics.length === 0 || selected.size > 0;

  return (
    <section className="screen-setup">
      <div className="setup-head">
        <div>
          <div className="eyebrow">Speaking practice</div>
          <h1 className="setup-title">Sit down with the examiner.</h1>
          <p className="setup-sub">
            Choose a drill and an accent. You'll hear each question spoken aloud, then answer out loud — just like the
            real interview.
          </p>
          <div className="provider-line">
            <span className={`dotmark ${tts.provider === 'browser' ? 'fallback' : 'live'}`} />
            {tts.provider === 'workers' ? (
              <span>Examiner voice ready.</span>
            ) : tts.provider === 'openai' || tts.provider === 'elevenlabs' ? (
              <span>Natural examiner voice ready ({tts.provider === 'openai' ? 'OpenAI' : 'ElevenLabs'}).</span>
            ) : tts.usingFallback ? (
              <span>Using your browser's voice — <button className="linkbtn" onClick={onOpenSettings}>add a key</button> for a natural examiner.</span>
            ) : (
              <span>Using your browser's voice.</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="icon-btn" title="Settings & keys" aria-label="Settings and keys" onClick={onOpenSettings}>⚙</button>
          <button className="icon-btn" title="Help" aria-label="Help" onClick={onOpenHelp}>?</button>
        </div>
      </div>

      <div className="section-label">Choose a drill</div>
      <div className="modes">
        {MODES.map((m) => (
          <button
            key={m.id}
            className={`mode${mode === m.id ? ' sel' : ''}`}
            onClick={() => setMode(m.id)}
            aria-pressed={mode === m.id}
          >
            <h3>{m.title}</h3>
            <p>{m.desc}</p>
            <span className="radio" />
          </button>
        ))}
      </div>

      <div className="section-label">Examiner's accent</div>
      <div className="pills">
        {ACCENTS.map((a) => (
          <button key={a} className={`pill${accent === a ? ' sel' : ''}`} onClick={() => setAccent(a)} aria-pressed={accent === a}>
            {ACCENT_LABEL[a]}
          </button>
        ))}
      </div>

      {showTopics && (
        <>
          <div className="section-label">
            Topics
            {!canStart && (
              <span style={{ color: 'var(--signal)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> — pick at least one</span>
            )}
          </div>
          <div className="chips">
            {allTopics.map((t) => (
              <button key={t} className={`chip${selected.has(t) ? ' sel' : ''}`} onClick={() => toggleTopic(t)} aria-pressed={selected.has(t)}>
                {t}
              </button>
            ))}
          </div>
        </>
      )}

      {showCount && (
        <>
          <div className="section-label">How many questions</div>
          <div className="pills">
            {[6, 12, 20].map((n) => (
              <button key={n} className={`pill${count === n ? ' sel' : ''}`} onClick={() => setCount(n)} aria-pressed={count === n}>
                {n}
              </button>
            ))}
          </div>
        </>
      )}

      {mode === 'full_mock' && (
        <p className="setup-note">A full mock runs ~14 minutes: a Part 1 warm-up, one Part 2 long-turn cue card, then a Part 3 discussion.</p>
      )}
      {mode === 'endless' && (
        <p className="setup-note">Endless keeps asking Part 1 questions until you press <b>End &amp; review</b>. Good for warming up.</p>
      )}

      <div className="start-row">
        <button className="btn-primary" onClick={begin} disabled={!canStart}>Start speaking</button>
        <span className="keyhint">or press <b>Space</b> to begin</span>
      </div>
      <div className="vstr">v{APP_VERSION}</div>
    </section>
  );
}
